import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Crown from "lucide-react/dist/esm/icons/crown";
import Zap from "lucide-react/dist/esm/icons/zap";
import Star from "lucide-react/dist/esm/icons/star";
import type { SubscriptionTier } from "@/lib/subscription-tiers";

interface SubscriptionBadgeProps {
  tier: SubscriptionTier;
  size?: "sm" | "md" | "lg";
  showTooltip?: boolean;
  className?: string;
}

const config: Record<string, { icon: typeof Crown; label: string; classes: string }> = {
  seller_pro: {
    icon: Zap,
    label: "Pro Seller",
    classes: "text-primary bg-primary/10 border-primary/20",
  },
  seller_elite: {
    icon: Crown,
    label: "Elite Seller",
    classes: "text-accent bg-accent/10 border-accent/20",
  },
  seller_max: {
    icon: Crown,
    label: "Max Seller",
    classes: "text-warning bg-warning/10 border-warning/20",
  },
  buyer_plus: {
    icon: Star,
    label: "Plus Member",
    classes: "text-chart-2 bg-chart-2/10 border-chart-2/20",
  },
};

const sizeClasses = {
  sm: "text-2xs px-1.5 py-0.5 gap-0.5",
  md: "text-xs px-2 py-0.5 gap-1",
  lg: "text-sm px-2.5 py-1 gap-1",
};

const iconSizes = { sm: "h-2.5 w-2.5", md: "h-3 w-3", lg: "h-3.5 w-3.5" };

export function SubscriptionBadge({ tier, size = "sm", showTooltip = true, className }: SubscriptionBadgeProps) {
  const cfg = config[tier];
  if (!cfg) return null;

  const Icon = cfg.icon;
  const badge = (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-bold uppercase tracking-wider",
        sizeClasses[size],
        cfg.classes,
        className
      )}
    >
      <Icon className={iconSizes[size]} />
      {cfg.label.split(" ")[0]}
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent><p>{cfg.label}</p></TooltipContent>
    </Tooltip>
  );
}
